// Shared text-font catalogue used by the video editor (text clips) and the
// anuncio mode (per-text font selection). Single source of truth so both
// surfaces always offer the exact same families.
//
// All families are self-hosted via @fontsource and imported globally in
// src/fonts.ts, so any font listed here is available app-wide (previews
// render correctly without extra loading).
//
// To add a new font: import its weights in src/fonts.ts, then add the family
// here.

export type FontCategory =
  | "sans"
  | "serif"
  | "display"
  | "handwriting"
  | "mono";

export const TEXT_FONT_OPTIONS: Array<{
  family: string;
  category: FontCategory;
  label?: string;
}> = [
  { family: "Inter", category: "sans" },
  { family: "Poppins", category: "sans" },
  { family: "Montserrat", category: "sans" },
  { family: "Roboto", category: "sans" },
  { family: "Playfair Display", category: "serif" },
  { family: "Bebas Neue", category: "display" },
  { family: "Oswald", category: "display" },
  { family: "Lobster", category: "handwriting" },
  { family: "Caveat", category: "handwriting" },
  { family: "Pacifico", category: "handwriting" },
  // Subtitle / viral-caption pack (TikTok / Reels / Shorts / MrBeast style).
  { family: "Anton", category: "display" },
  { family: "Fjalla One", category: "display" },
  { family: "Archivo Black", category: "display" },
  { family: "Russo One", category: "display" },
  { family: "Bangers", category: "display" },
  { family: "Luckiest Guy", category: "display" },
  { family: "Permanent Marker", category: "handwriting" },
];

// Build a CSS font-family value with a category-appropriate fallback so a
// preview never collapses to Times New Roman if the font is still loading or
// the user is offline.
export const fontStackFor = (family?: string): string => {
  const fam = family && family.trim() ? family.trim() : "Inter";
  const opt = TEXT_FONT_OPTIONS.find(
    (o) => o.family.toLowerCase() === fam.toLowerCase(),
  );
  const fallback = (() => {
    switch (opt?.category) {
      case "serif":
        return 'Georgia, "Times New Roman", serif';
      case "display":
        return '"Impact", "Arial Black", system-ui, sans-serif';
      case "handwriting":
        return '"Comic Sans MS", "Brush Script MT", cursive';
      case "mono":
        return "Menlo, Consolas, monospace";
      default:
        return 'system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif';
    }
  })();
  return `"${fam}", ${fallback}`;
};
